import type { OrderDiscountMode } from 'shared-models/browser';
import { roundMoney } from '../../../../../utils/money';
import type { OrderlineView } from '../../../../../services/orderlines/orderlines.service';
import type { PaymentlineView } from '../../../../../services/payments/payments.service';
import type { DiscountMode, DiscountScope } from '../../receipt/actions/global-discount-modal/GlobalDiscountModal';
import type { OrderlineSelection } from '../../receipt/actions/item-discount-select-modal/ItemDiscountSelectModal';

export type ItemDiscountPayload = {
  orderlineId: number;
  quantity: number;
  discountInput: number | null;
};

export type AppliedItemDiscountPayload = {
  orderlineId: number;
  quantity: number;
  discountInput: number;
};

const roundDiscount = roundMoney;

export const calculateOrderDiscount = (
  cartTotal: number,
  discountMode: OrderDiscountMode | null | undefined,
  discountInput: number | null | undefined,
  roundAmounts?: boolean,
): number => {
  if (!discountMode || discountInput == null) {
    return 0;
  }

  const raw = discountMode === 'amount'
    ? discountInput
    : (cartTotal * discountInput) / 100;

  // Match backend NUMERIC(12,1) persistence so the FE preview cannot show a value that
  // cannot exist in stored state. Ref: docs/function-library/web/contexts-pages.md (discount flow).
  return roundMoney(Math.min(raw, cartTotal), roundAmounts);
};

export const resolveGlobalDiscountPersistence = (
  mode: DiscountMode,
  scope: DiscountScope,
  discountAmount: number,
  discountInput: number,
): { discountMode: OrderDiscountMode; discountInput: number } => ({
  discountMode: scope === 'snapshot' || mode === 'amount' ? 'amount' : mode,
  discountInput: scope === 'snapshot' || mode === 'amount' ? discountAmount : discountInput,
});

export const resolveGlobalDiscountScopeForPayments = (
  scope: DiscountScope,
  paidAmount: number,
): DiscountScope => (paidAmount > 0 ? 'snapshot' : scope);

export const getRemainingAfterGlobalDiscount = (
  remainingToPayMinusTips: number,
  globalDiscount: number,
): number => Math.max(0, remainingToPayMinusTips - globalDiscount);

export type OrderMoneyBreakdown = {
  cartTotal: number;
  paidAmount: number;
  discount: number;
  remainingAfterGlobalDiscount: number;
};

export type OrderMoneyBreakdownParams = {
  order: { roundAmounts?: boolean; discountMode?: OrderDiscountMode | null; discountInput?: number | null } | null | undefined;
  orderlines: OrderlineView[];
  paymentlines: PaymentlineView[];
  // Take-away bills its unsent (pending) lines too (paid upfront, sent to kitchen at close).
  isTakeAway: boolean;
};

// Discount ceiling excludes tips by design - discounts apply to the bill, never the tip.
export const computeOrderMoneyBreakdown = ({
  order,
  orderlines,
  paymentlines,
  isTakeAway,
}: OrderMoneyBreakdownParams): OrderMoneyBreakdown => {
  const cartTotal = orderlines
    .filter((line) => line.status !== 'pending' || isTakeAway)
    .reduce((sum, line) => sum + roundMoney(line.unitPrice * line.quantity - (line.discountInput ?? 0), order?.roundAmounts), 0);
  const paidAmount = paymentlines
    .filter((line) => line.status === 'done' && !line.canceledAt)
    .reduce((sum, line) => sum + line.amount, 0);
  const discount = calculateOrderDiscount(cartTotal, order?.discountMode, order?.discountInput, order?.roundAmounts);

  return {
    cartTotal,
    paidAmount,
    discount,
    remainingAfterGlobalDiscount: getRemainingAfterGlobalDiscount(cartTotal - paidAmount, discount),
  };
};

export const getSelectedLine = (
  orderlines: OrderlineView[],
  selection: OrderlineSelection,
): OrderlineView | undefined => orderlines.find((line) => line.id === selection.orderlineId);

export const getMaxPerUnitDiscount = (
  selections: OrderlineSelection[],
  orderlines: OrderlineView[],
): number => {
  if (selections.length === 0) {
    return 0;
  }

  return Math.min(
    ...selections.map((selection) => getSelectedLine(orderlines, selection)?.unitPrice ?? 0),
  );
};

export const hasExistingItemDiscount = (
  selections: OrderlineSelection[],
  orderlines: OrderlineView[],
): boolean => selections.some((selection) => {
  const line = getSelectedLine(orderlines, selection);
  return line?.discountInput != null && line.discountInput > 0;
});

export const buildCompedItemDiscountPayload = (
  selections: OrderlineSelection[],
  orderlines: OrderlineView[],
): Array<AppliedItemDiscountPayload & { existingDiscount: number }> => selections.map((selection) => {
  const line = getSelectedLine(orderlines, selection);
  const unitPrice = line?.unitPrice ?? 0;
  // Override semantics: stored discount becomes the full gross so the comped line nets 0.
  return {
    orderlineId: selection.orderlineId,
    quantity: selection.quantity,
    discountInput: roundDiscount(unitPrice * selection.quantity),
    existingDiscount: line?.discountInput ?? 0,
  };
});

export const buildItemDiscountPayload = (
  selections: OrderlineSelection[],
  orderlines: OrderlineView[],
  discountValue: number,
  mode: DiscountMode,
): Array<AppliedItemDiscountPayload & { existingDiscount: number }> => selections.map((selection) => {
  const line = getSelectedLine(orderlines, selection);
  const unitPrice = line?.unitPrice ?? 0;
  const discountInput = mode === 'amount'
    ? roundDiscount(discountValue * selection.quantity)
    : roundDiscount(unitPrice * selection.quantity * (discountValue / 100));

  return {
    orderlineId: selection.orderlineId,
    quantity: selection.quantity,
    discountInput,
    existingDiscount: line?.discountInput ?? 0,
  };
});

export const getTotalDiscountDelta = (
  items: Array<{ discountInput: number; existingDiscount: number }>,
): number => items.reduce((sum, item) => sum + item.discountInput - item.existingDiscount, 0);
